import { useState, useMemo } from "react";

const MONTHS_DE = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember",
];
const DAYS_DE = ["Mo","Di","Mi","Do","Fr","Sa","So"];
const DAY_NAMES_FULL = ["Sonntag","Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag"];
const TIME_SLOTS = [
  "09:00 Uhr","10:00 Uhr","11:00 Uhr",
  "14:00 Uhr","15:00 Uhr","16:00 Uhr",
];

interface DateTimePickerProps {
  onChange: (value: string) => void;
}

export default function DateTimePicker({ onChange }: DateTimePickerProps) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>("");

  const days = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(viewYear, viewMonth, d));
    }
    return cells;
  }, [viewYear, viewMonth]);

  const isDisabled = (date: Date) =>
    date < today || date.getDay() === 0;

  const canGoPrev =
    viewYear > today.getFullYear() || viewMonth > today.getMonth();

  const prevMonth = () => {
    if (!canGoPrev) return;
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const handleDateSelect = (date: Date) => {
    if (isDisabled(date)) return;
    setSelectedDate(date);
    setSelectedTime("");
    onChange("");
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      const formatted = `${DAY_NAMES_FULL[selectedDate.getDay()]}, ${selectedDate.getDate()}. ${MONTHS_DE[selectedDate.getMonth()]} ${selectedDate.getFullYear()} um ${time}`;
      onChange(formatted);
    }
  };

  const isSelected = (date: Date) =>
    selectedDate?.toDateString() === date.toDateString();

  const isToday = (date: Date) =>
    date.toDateString() === today.toDateString();

  return (
    <div className="flex flex-col gap-5">
      {/* Calendar card */}
      <div className="border border-light-gray bg-white">
        {/* Month header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-light-gray">
          <button
            type="button"
            onClick={prevMonth}
            disabled={!canGoPrev}
            className={`w-8 h-8 flex items-center justify-center transition-colors duration-200 ${
              canGoPrev
                ? "text-text-secondary hover:text-gold cursor-pointer"
                : "text-light-gray cursor-not-allowed"
            }`}
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>

          <span className="font-serif text-sm text-text-primary font-medium tracking-wide">
            {MONTHS_DE[viewMonth]} {viewYear}
          </span>

          <button
            type="button"
            onClick={nextMonth}
            className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-gold transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>

        <div className="px-4 py-4">
          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS_DE.map((d) => (
              <div
                key={d}
                className={`text-center text-xs font-sans font-medium uppercase tracking-wider py-1 ${
                  d === "So" ? "text-text-muted/30" : "text-text-muted"
                }`}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {days.map((date, i) => {
              if (!date) return <div key={`e-${i}`} className="h-9" />;
              const disabled = isDisabled(date);
              const selected = isSelected(date);
              const todayMark = isToday(date);
              const sunday = date.getDay() === 0;
              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleDateSelect(date)}
                  className={`
                    h-9 w-full flex items-center justify-center text-xs font-sans
                    transition-all duration-150 rounded-sm
                    ${selected ? "bg-gold text-white font-semibold" : ""}
                    ${!selected && !disabled ? "hover:bg-gold/8 hover:text-gold cursor-pointer" : ""}
                    ${disabled ? "text-text-muted/25 cursor-not-allowed" : ""}
                    ${todayMark && !selected ? "ring-1 ring-gold/50 text-gold font-medium" : ""}
                    ${!selected && !disabled && !todayMark && !sunday ? "text-text-primary" : ""}
                    ${sunday && !disabled ? "text-text-muted/30 cursor-not-allowed" : ""}
                  `}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="px-6 py-3 border-t border-light-gray flex items-center gap-5">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-gold" />
            <span className="text-xs font-sans text-text-muted">Ausgewählt</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm ring-1 ring-gold/50" />
            <span className="text-xs font-sans text-text-muted">Heute</span>
          </div>
          <span className="text-xs font-sans text-text-muted/50 ml-auto">Sonntage nicht verfügbar</span>
        </div>
      </div>

      {/* Time slots — appear after date is selected */}
      {selectedDate && (
        <div className="animate-fade-up">
          <p className="text-xs tracking-luxury font-sans text-text-muted uppercase mb-4">
            Bevorzugte Uhrzeit —&nbsp;
            <span className="text-text-secondary normal-case tracking-normal">
              {DAY_NAMES_FULL[selectedDate.getDay()]}, {selectedDate.getDate()}. {MONTHS_DE[selectedDate.getMonth()]}
            </span>
          </p>
          <div className="grid grid-cols-3 gap-2">
            {TIME_SLOTS.map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => handleTimeSelect(slot)}
                className={`py-3 text-xs font-sans font-medium tracking-wider border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedTime === slot
                    ? "border-gold bg-gold/5 text-gold"
                    : "border-light-gray text-text-muted hover:border-gold hover:text-gold"
                }`}
              >
                {slot}
              </button>
            ))}
          </div>

          {selectedTime && (
            <div className="mt-4 flex items-start gap-3 p-4 bg-warm-white border-l-2 border-gold">
              <i className="ri-calendar-check-line text-gold text-base mt-0.5 flex-shrink-0" />
              <p className="text-xs font-sans text-text-secondary leading-relaxed">
                Gewünschter Rückruf-Termin:{" "}
                <span className="font-medium text-text-primary">
                  {DAY_NAMES_FULL[selectedDate.getDay()]}, {selectedDate.getDate()}. {MONTHS_DE[selectedDate.getMonth()]} {selectedDate.getFullYear()} um {selectedTime}
                </span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
